import {
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    IterableDiffers,
    ViewChild,
} from '@angular/core';
import { ElementAttribute, ElementTagName } from '@models/editor';
import { CanvasInputMode } from '@service/canvas/canvas-input-mode.service';
import { RectangleService } from '@service/canvas/rectangle.service';
import { TriangleDrawHelperService } from '@service/canvas/triangle-draw-helper.service';
import { defaultNumberInputArrowedKey, KeyFilterSerivce } from '@service/key-filter.service';
import { mixedString } from 'icreate/common/models/mimic';

@Component({
    selector: 'app-corner-radius-options',
    templateUrl: './corner-radius-options.component.html',
    styleUrls: ['./../design-side-menu/design-side-menu.component.css', './corner-radius-options.component.scss'],
    standalone: false,
})
export class CornerRadiusOptionsComponent {
    readonly ALLOWED_KEYS = defaultNumberInputArrowedKey;
    readonly TOP_RIGHT_CORNER = RectangleCorners.TOP_RIGHT_CORNER;
    readonly TOP_LEFT_CORNER = RectangleCorners.TOP_LEFT_CORNER;
    readonly BOTTOM_RIGHT_CORNER = RectangleCorners.BOTTOM_RIGHT_CORNER;
    readonly BOTTOM_LEFT_CORNER = RectangleCorners.BOTTOM_LEFT_CORNER;
    readonly MIXED_STRING = mixedString;

    @Input() selectedElements;
    @ViewChild('radiusInput') radiusInput: ElementRef;

    radius: string;
    corners: {
        [RectangleCorners.TOP_RIGHT_CORNER]: string;
        [RectangleCorners.TOP_LEFT_CORNER]: string;
        [RectangleCorners.BOTTOM_RIGHT_CORNER]: string;
        [RectangleCorners.BOTTOM_LEFT_CORNER]: string;
    } = {
        [RectangleCorners.TOP_RIGHT_CORNER]: '0',
        [RectangleCorners.TOP_LEFT_CORNER]: '0',
        [RectangleCorners.BOTTOM_RIGHT_CORNER]: '0',
        [RectangleCorners.BOTTOM_LEFT_CORNER]: '0',
    };

    showRadiusOption: boolean = false;
    shouldIgnoreBlur: boolean = false;
    cornerMaxValue: number;
    iterableDiffer;
    isRectangleSelected = false;

    constructor(
        private iterableDiffers: IterableDiffers,
        private triangleService: TriangleDrawHelperService,
        private keyFilterService: KeyFilterSerivce,
        private rectangleService: RectangleService,
        private cdr: ChangeDetectorRef
    ) {
        this.iterableDiffer = iterableDiffers.find([]).create(null);
    }

    @HostListener('document:click', ['$event'])
    handleClickOut(event: MouseEvent) {
        if (!this.radiusInput.nativeElement.contains(event.target)) {
            this.radiusInput.nativeElement.value = this.radius;
            this.shouldIgnoreBlur = true;
            this.radiusInput.nativeElement.blur();
        }
    }

    ngDoCheck() {
        let changes = this.iterableDiffer.diff(this.selectedElements);
        if (changes) {
            this.shouldIgnoreBlur = false;
            if (this.selectedElements[0].getAttribute(ElementAttribute.type) === CanvasInputMode.TRIANGLE) {
                this.isRectangleSelected = false;
                const trangleRadius = this.selectedElements[0].getAttribute('border-radius');
                this.radius = trangleRadius ? trangleRadius : '0';
            } else if (this.selectedElements[0].tagName == ElementTagName.rect) {
                this.isRectangleSelected = true;
                this.radius = this.selectedElements[0].getAttribute(ElementAttribute.rx) || '0';
                this.corners = {
                    [RectangleCorners.TOP_RIGHT_CORNER]: this.radius,
                    [RectangleCorners.TOP_LEFT_CORNER]: this.radius,
                    [RectangleCorners.BOTTOM_RIGHT_CORNER]: this.radius,
                    [RectangleCorners.BOTTOM_LEFT_CORNER]: this.radius,
                };
                const rectHeight = this.selectedElements[0].getAttribute(ElementAttribute.height);
                const rectWidth = this.selectedElements[0].getAttribute(ElementAttribute.width);
                this.cornerMaxValue = Math.max(rectHeight, rectWidth) / 2;
            } else {
                this.isRectangleSelected = true;
                this.radius = mixedString;
                const rectHeight = this.selectedElements[0].getAttribute(ElementAttribute.height);
                const rectWidth = this.selectedElements[0].getAttribute(ElementAttribute.width);
                this.cornerMaxValue = Math.min(rectHeight, rectWidth) / 2;
                this.corners = this.rectangleService.getRectangleCorners(this.selectedElements[0]);
            }
        }
    }

    handleRadiusChange(event) {
        if (this.shouldIgnoreBlur) {
            this.shouldIgnoreBlur = false;
            return;
        }
        if (event?.target?.value == mixedString) return;
        let value = Number(event?.target?.value)?.toString();
        if (this.radius == value) {
            this.radiusInput.nativeElement.value = value;
            return;
        }
        if (!value || !Number(value)) value = '0';
        if (Number(value) < 0) value = '0';
        const bbox = this.selectedElements[0].getBBox();
        const rectHeight = bbox.height;
        const rectWidth = bbox.width;
        this.cornerMaxValue = Math.min(rectHeight, rectWidth) / 2;
        if (Number(value) > this.cornerMaxValue) value = this.cornerMaxValue.toString();
        this.selectedElements.forEach((element: HTMLElement) => {
            if (element.tagName === ElementTagName.rect) {
                element.setAttribute(ElementAttribute.rx, value);
            } else if (element?.getAttribute(ElementAttribute.type) === CanvasInputMode.TRIANGLE) {
                this.triangleService.changeRadius(element, value);
            } else if (element?.getAttribute(ElementAttribute.type) === CanvasInputMode.RECTANGLE) {
                this.selectedElements[0] = this.rectangleService.redrawPathAsRectangle(element, value);
            }
        });
        this.corners = {
            [RectangleCorners.TOP_RIGHT_CORNER]: value,
            [RectangleCorners.TOP_LEFT_CORNER]: value,
            [RectangleCorners.BOTTOM_RIGHT_CORNER]: value,
            [RectangleCorners.BOTTOM_LEFT_CORNER]: value,
        };
        this.radius = value;
        this.radiusInput.nativeElement.value = value;
        this.cdr.detectChanges();
    }

    validRadiusKeyDown(event: KeyboardEvent, updateOnEnter = false) {
        this.shouldIgnoreBlur = false;
        if (event.key == 'Enter' && updateOnEnter) {
            this.handleRadiusChange(event);
            event.preventDefault();
            return;
        }
        if (this.checkStringLimit(this.radius) && event.key !== 'Backspace') {
            event.preventDefault();
            return;
        }

        if (event.key == ',' && updateOnEnter) {
            event.preventDefault();
            return;
        }

        const shouldBlockKey = this.keyFilterService.shouldPreventDefault(event, [...this.ALLOWED_KEYS]);
        if (shouldBlockKey) event.preventDefault();
    }

    checkStringLimit(value) {
        return String(value).length >= 20;
    }

    handleIconClick() {
        if (this.selectedElements.length == 0) return;
        if (this.selectedElements[0]?.getAttribute(ElementAttribute.type) === CanvasInputMode.TRIANGLE) return;
        this.showRadiusOption = !this.showRadiusOption;
    }

    cornerRadiusChanged(corner: RectangleCorners, value: string) {
        const element = this.selectedElements[0];
        const rectHeight = this.selectedElements[0].getAttribute(ElementAttribute.height);
        const rectWidth = this.selectedElements[0].getAttribute(ElementAttribute.width);
        this.cornerMaxValue = Math.min(rectHeight, rectWidth) / 2;
        if (Number(value) > this.cornerMaxValue) value = this.cornerMaxValue.toString();
        const simplifiedValue = Number(value)?.toString();
        this.corners[corner] = simplifiedValue;

        let newElement;
        if (this.cornersAreTheSame()) {
            newElement = this.rectangleService.redrawPathAsRectangle(element, simplifiedValue);
        } else if (element.tagName == ElementTagName.rect) {
            newElement = this.rectangleService.redrawRectangleAsPath(element, this.corners);
        } else {
            newElement = this.rectangleService.redrawPath(element, this.corners);
        }
        this.selectedElements[0] = newElement;
    }

    private cornersAreTheSame() {
        const values = Object.values(this.corners);
        return values.every(value => Number(value) === Number(values[0]));
    }
}

export enum RectangleCorners {
    UNDEFINED = 'UNDEFINED',
    TOP_RIGHT_CORNER = 'top-right',
    TOP_LEFT_CORNER = 'top-left',
    BOTTOM_RIGHT_CORNER = 'bottom-right',
    BOTTOM_LEFT_CORNER = 'bottom-left',
}
